import React, { createContext, useCallback, useContext } from 'react';

/**
 * Scene anchors tie DOM sections to the canvas choreography.
 * The renderer measures the registered elements every frame, so sections can
 * be inserted, removed, or resized without touching the canvas code — the sky
 * palette and orb keyframes are re-derived from live layout.
 */
export type SceneAnchorKey = 'asr' | 'tts' | 'slm' | 'conv';

export type SceneAnchorMap = Map<SceneAnchorKey, HTMLElement>;

export interface SceneContextValue {
  anchors: React.MutableRefObject<SceneAnchorMap>;
  registerAnchor: (key: SceneAnchorKey, el: HTMLElement | null) => void;
}

export const SceneContext = createContext<SceneContextValue | null>(null);

export const useSceneAnchors = (): SceneContextValue => {
  const ctx = useContext(SceneContext);
  if (!ctx) throw new Error('useSceneAnchors must be used inside a SceneProvider');
  return ctx;
};

/** Ref callback that registers a section element under the given anchor key. */
export const useSceneAnchor = (key: SceneAnchorKey) => {
  const { registerAnchor } = useSceneAnchors();
  return useCallback((el: HTMLElement | null) => registerAnchor(key, el), [key, registerAnchor]);
};
